import {
  ContextualSaveBar,
  Card,
  Select,
  Text,
  BlockStack,
  Page,
  Box,
  SkeletonDisplayText,
} from "@medentic/polaris-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BGRComplianceData, Country, UserComplianceData } from "../types";
import { useState } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";
import { AppPageWrapper } from "@shared/ui";
import { Forms } from "./Forms";
import { CountryAPIMapping } from "../CountryAPIMapping";

const UserCompliancePage = () => {
  const { t } = useTranslation();
  const { GET, PUT } = useApiRequest();

  const [selectedCountryOverride, setSelectedCountryOverride] = useState<
    string | null
  >(null);
  const [formState, setFormState] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    BGRComplianceData | Record<string, any>
  >({});
  const [showSaveBar, setShowSaveBar] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showValidationError, setShowValidationError] = useState(false);

  const { countries: countriesData, isLoading: isCountriesLoading } = useCountries();

  const { data: userComplianceData, isLoading: isComplianceLoading } = useQuery(
    {
      queryKey: ["user-compliance"],
      queryFn: () => GET<UserComplianceData>("/api/user/me/compliance"),
    }
  );

  const supportedCountries = countriesData?.filter((c: Country) =>
    userComplianceData?.availableCountries.includes(c.iso3)
  );

  const countryOptions = [
    { name: t("userCompliance.none"), code: "None" },
    ...(supportedCountries || []).map((c) => ({ name: c.name, code: c.iso3 })),
  ];

  const selectedCountry =
    selectedCountryOverride ?? userComplianceData?.country ?? "None";

  const saveComplianceMutation = useMutation({
    mutationFn: async () => {
      if (selectedCountry === "None") {
        await PUT("/api/user/me/compliance", { country: null });
      } else {
        await PUT("/api/user/me/compliance", { country: selectedCountry });
        await PUT(`/api/compliance${CountryAPIMapping[selectedCountry]}`, {
          ...formState,
        });
      }
    },
    onSuccess: () => {
      setShowSaveBar(false);
      setShowValidationError(false); // Reset submission state after successful save
    },
  });

  const handleCountryChange = (value: string) => {
    setSelectedCountryOverride(value);
    setFormState({});
    setValidationErrors({});
    setShowSaveBar(true);
  };

  const handleSave = () => {
    setShowValidationError(true);

    // Prevent save if there are validation errors
    if (Object.keys(validationErrors).length > 0) {
      console.error("Validation errors:", validationErrors);
      return;
    }

    // Trigger save mutation if no validation errors
    saveComplianceMutation.mutate();
  };

  return (
    <AppPageWrapper>
      {showSaveBar && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          message={t("userCompliance.contextualSaveBar.message")}
          saveAction={{
            onAction: handleSave,
            loading: saveComplianceMutation.isPending,
          }}
          discardAction={{
            onAction: () => setShowSaveBar(false),
          }}
        />
      )}
      <Page title={t("userCompliance.title")} narrowWidth>
        <BlockStack gap={"400"}>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                {t("userCompliance.cardTitle")}
              </Text>
              <Text as="p">{t("userCompliance.description")}</Text>
              {isCountriesLoading || isComplianceLoading ? (
                <SkeletonDisplayText />
              ) : (
                <Box maxWidth="40%" paddingBlockStart={"200"}>
                  <Select
                    label=""
                    options={countryOptions.map((c) => ({
                      label: c.name,
                      value: c.code,
                    }))}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                  />
                </Box>
              )}
            </BlockStack>
          </Card>
          {selectedCountry !== "None" && (
            <Card>
              {Forms[selectedCountry]?.({
                formState: formState as BGRComplianceData,
                setFormState,
                setShowSaveBar,
                setValidationErrors,
                showValidationError,
              })}
            </Card>
          )}
        </BlockStack>
      </Page>
      <Box paddingBlockEnd={"400"} />
    </AppPageWrapper>
  );
};

export default UserCompliancePage;
